import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';

import { Observable, of, EMPTY, Subject } from 'rxjs';
import { catchError, tap, switchMap, map, takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import moment from 'moment-timezone';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AgentsApiService } from '../../../../api/agents/agents.api';
import { PrecisionClipApi, PrecisionClipResponse } from '../../../../api/agents/models/precision-clip.api.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { TVEyesMentionsService } from '../../../../Common/Mentions/services/tveyes-mentions.service';
import { TVEyesPlayerletteUrl, MediaEditorTab } from '../../../../Common/Mentions/models/tveyes-playerlette-url.model';
import { MentionInsightModel } from '../../../../Common/Mentions/models/mention-insight.model';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { SaveMentionsRequestModel } from '../../../../api/mentions/model/save-mentions-request.model';
import { TVEyesArchivedStateEnum } from '../../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { MentionsService } from '../../../Core/services/mentions.service';
import { MentionIdMercuryMapModel } from '../../../../api/mentions/model/mention-id-mercury-map.model';
import { MentionSearchModel } from '../../models/mention-search.model';
import { SaveMentionsModel } from '../../models/save-mentions.model';
import { KeywordSelectionEnum } from '../../enums/precision-keyword-selection.enum';


@Component({
    selector: 'agpr-precision-clip-setup-dialog',
    templateUrl: './precision-clip-setup-dialog.component.html',
    styleUrls: ['./precision-clip-setup-dialog.component.scss'],
    standalone: false
})
export class PrecisionClipSetupDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() mention: Mention;
    /** @HACK - mentionSearch is needed for providing saved context for saving non-saved mentions */
    @Input() mentionSearch: MentionSearchModel;

    keywordSelection: KeywordSelectionEnum = KeywordSelectionEnum.SAVED_MENTION;
    manualKeywords: string[] = [];
    mentionKeywords: string[] = [];

    insightsEnabled: boolean = true;

    extendStartTime: number = 5;
    extendedEndTime: number = 5;

    loading: boolean = false;
    isPreview: boolean = false;

    readonly id = 'precisionClipSetupDialog';
    readonly buttonType = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly keywordSelectionEnum = KeywordSelectionEnum;
    readonly loadingSteps = [
        'Monitor.precisionClip.loading.step1',
        'Monitor.precisionClip.loading.step2',
        'Monitor.precisionClip.loading.step3',
        'Monitor.precisionClip.loading.step4',
    ];

    currentLoadingMessage: string = this.loadingSteps[0];

    private loadingTimers: ReturnType<typeof setTimeout>[] = [];
    private readonly estimatedLoadTimeMs = 12000;

    private cancel$ = new Subject<void>();
    private startTime: moment.Moment;

    constructor(
        public coreDialogService: CoreDialogService,
        private agentApiService: AgentsApiService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private tvEyesMentionsService: TVEyesMentionsService,
        private mentionApiService: MentionsApi,
        private mentionService: MentionsService
    ) {
        super(coreDialogService, 'PrecisionClipSetup');
    }

    ngOnInit() {
        this.mentionKeywords = this.mention.contextualKeywords.length
            ? this.mention.contextualKeywords : this.mention?.matchedKeywords ?? [];

        if (this.mentionKeywords.length === 0) {
            this.keywordSelection = KeywordSelectionEnum.MANUAL;
        }

        const params = new URLSearchParams(this.mention.url);
        this.startTime = moment.utc(params.get(TVEyesPlayerletteUrl.START_DATE_TIME));
    }


    /**
     * Fetch the precision clip data and save the mention (to get a mentionId) if not already saved.
     * Open the Media Editor dialog and close this dialog.
     */
    preview() {
        this.isPreview = true;
        this.loading = true;
        this.startLoadingSequence();

        this.getPrecisionClip().pipe(
            switchMap((response: PrecisionClipResponse) => {
                if (!this.mention.mentionId) {
                    return this.saveMention().pipe(map(() => response));
                } else {
                    return of(response);
                }
            }),
            switchMap((response: PrecisionClipResponse) => {
                const startDateTime = this.startTime.clone().add(response.primary.adjustedStartTimestamp, 'seconds');
                const editorEndDateTime = startDateTime.clone().add(response.primary.durationSeconds, 'seconds');

                return this.getPlayerletteUrl(
                    this.mention.url,
                    startDateTime,
                    response.primary.durationSeconds,
                    startDateTime,
                    editorEndDateTime,
                    MediaEditorTab.EDITOR
                );
            }),
            catchError((error) => {
                this.loading = false;
                this.isPreview = false;
                this.clearLoadingTimers();

                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: 'Monitor.precisionClip.preview.error.message',
                });

                throw error;
            }),
            switchMap((url) => {
                const keywords = this.keywordSelection === KeywordSelectionEnum.MANUAL
                    ? this.manualKeywords : this.mentionKeywords;

                return this.tvEyesMentionsService.openTVEyesMediaViewDialog(this.mention, url, this.mentionSearch, keywords).pipe(
                    tap((x) => {
                        this.loading = false;
                        this.isPreview = false;
                        this.clearLoadingTimers();
                        this.closeDialog();
                    }),
                );
            }),
            takeUntil(this.cancel$)
        ).subscribe();
    }


    /**
     * Fetch the precision clip data and save the mention (to get a mentionId) if not already saved.
     * Patch the mention if insight generation is enabled and save the new playerlett clipped url to the mention.
     */
    confirm() {
        this.loading = true;
        this.startLoadingSequence();

        this.getPrecisionClip().pipe(
            switchMap((response: PrecisionClipResponse) => {
                if (!this.mention.mentionId) {
                    return this.saveMention().pipe(map(() => response));
                } else {
                    return of(response);
                }
            }),
            switchMap((response: PrecisionClipResponse) => {
                if (this.insightsEnabled) {
                    const newInsight = new MentionInsightModel();
                    newInsight.name = 'Clip Insights';
                    newInsight.content = `${response.primary.rationale}<br>Segment Type: ${response.primary.segmentType}`;
                    newInsight.isPreferred = true;

                    if (!this.mention.insights) {
                        this.mention.insights = [];
                    }
                    this.mention.insights.push(newInsight);

                    return this.mentionApiService.patchMention(this.mention.convertToApiMention()).pipe(
                        map(() => response)
                    );
                } else {
                    return of(response);
                }
            }),
            switchMap((response: PrecisionClipResponse) => {
                const existing = TVEyesPlayerletteUrl.fromUrl(this.mention.url);

                const startDateTime = this.startTime.clone().add(response.primary.adjustedStartTimestamp, 'seconds');

                const newPlayerletteUrl = new TVEyesPlayerletteUrl(
                    this.mention.url,
                    startDateTime,
                    response.primary.durationSeconds,
                    existing.getStationId(),
                ).getMentionUrl();

                return this.mentionApiService.patchTVEyesMention({
                    id: this.mention.mentionId,
                    isDirectArchive: false,
                    clipDuration: response.primary.durationSeconds,
                    body: this.mention.body,
                    tveyesArchivedState: TVEyesArchivedStateEnum.EDITED,
                    tvEyesInstaPlayUrl: newPlayerletteUrl,
                    startDateTime: startDateTime.valueOf()
                }).pipe(map(() => newPlayerletteUrl));
            }),
            switchMap(() => {
                return this.mentionService.refetchMentions([this.mention.mentionId]);
            }),
            catchError((error) => {
                this.loading = false;
                this.clearLoadingTimers();

                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: this.localizationService.transform('Monitor.precisionClip.mention.error.message', {
                        name: this.mention.headline
                    }),
                });

                throw error;
            }),
            tap(() => {
                this.loading = false;
                this.clearLoadingTimers();

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    detail: this.localizationService.transform('Monitor.precisionClip.mention.success.message', {
                        name: this.mention.headline
                    }),
                });


                this.closeDialog();
            }),
            takeUntil(this.cancel$)
        ).subscribe();
    }


    cancel() {
        this.cancel$.next();

        this.loading = false;
        this.isPreview = false;
        this.clearLoadingTimers();

        this.closeDialog();
    }

    /** Save the mention and update its mentionId */
    private saveMention(): Observable<MentionIdMercuryMapModel[]> {
        const payload: SaveMentionsRequestModel = new SaveMentionsModel([this.mention], this.mentionSearch).getSaveNewMentionsRequestModel();

        return this.mentionApiService.createMentionsFromMercury(payload).pipe(
            tap((idMap) => this.mention.mentionId = idMap[0].mentionId),
            catchError((error) => {
                if (error.status === HttpStatusCode.BadRequest) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Monitor.precisionClip.error.saveMention.400.header',
                        detail: 'Monitor.precisionClip.error.saveMention.400.message',
                    });
                }

                this.loading = false;
                this.clearLoadingTimers();

                return EMPTY;
            })
        );
    }

    private getPrecisionClip(): Observable<PrecisionClipResponse> {
        const keywords = this.keywordSelection === KeywordSelectionEnum.MANUAL
            ? this.manualKeywords : this.mentionKeywords;

        const payload: PrecisionClipApi = {
            keywords: keywords,
            startOffsetSeconds: this.extendStartTime,
            endOffsetSeconds: this.extendedEndTime,
            playerletteUrl: this.mention.url
        };

        return this.agentApiService.getPrecisionClip(payload).pipe(
            catchError((error) => {
                this.loading = false;
                this.isPreview = false;
                this.clearLoadingTimers();

                if (error.status === HttpStatusCode.UnprocessableEntity) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Monitor.precisionClip.error.422.header',
                        detail: 'Monitor.precisionClip.error.422.message',
                    });
                } else if (error.status === HttpStatusCode.BadRequest) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Monitor.precisionClip.error.400.header',
                        detail: 'Monitor.precisionClip.error.400.message',
                    });
                }

                return EMPTY;
            })
        );
    }

    private getPlayerletteUrl(
        mentionUrl: string,
        desiredStart: moment.Moment,
        duration: number,
        startTime: moment.Moment,
        endTime: moment.Moment,
        defaultTab: MediaEditorTab
    ): Observable<string> {
        const existing = TVEyesPlayerletteUrl.fromUrl(mentionUrl);

        const keywords = this.keywordSelection === KeywordSelectionEnum.MANUAL
            ? this.manualKeywords : this.mentionKeywords;

        const unsignedPlayerletteUrl = new TVEyesPlayerletteUrl(
            mentionUrl,
            desiredStart,
            duration,
            existing.getStationId(),
            startTime,
            endTime,
            defaultTab
        ).getMentionUrl();

        return this.tvEyesMentionsService.getMediaViewUrlFromPlayerlette(
            this.mention,
            unsignedPlayerletteUrl,
            keywords,
            false
        ).pipe(
            catchError(err => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Mention.details.openMediaEditor.error.summary',
                    detail: 'Mention.details.openMediaEditor.error.details'
                });

                throw err;
            })
        );
    }

    /**
     * Start the loading message loop, scheduling every loading step expect the last (the last one just sticks)
     */
    private startLoadingSequence() {
        this.clearLoadingTimers();
        this.currentLoadingMessage = this.loadingSteps[0];

        const stepDuration = this.estimatedLoadTimeMs / this.loadingSteps.length;

        for (let i = 1; i < this.loadingSteps.length; i++) {
            this.loadingTimers.push(
                setTimeout(() => (this.currentLoadingMessage = this.loadingSteps[i]), stepDuration * i)
            );
        }
    }

    private clearLoadingTimers(): void {
        this.loadingTimers.forEach(clearTimeout);
        this.loadingTimers = [];
    }

    ngOnDestroy() {
        this.clearLoadingTimers();

        this.cancel$.next();
        this.cancel$.complete();
    }
}
